import { useEffect, useRef, useState } from 'react'
import { Monitor, RefreshCw, Smartphone } from 'lucide-react'

import { parsePreviewMessage, type BuilderToPreview, type CampaignDraft } from '@iws/theme-schema/preview-protocol'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { mintPreviewToken } from '@/lib/storefront'

type Device = 'mobile' | 'desktop'

interface Props {
  origin: string
  offer: { id: string; slug: string }
  draft: CampaignDraft
  /** Called once the merchant has looked at the phone view (feeds the pre-launch checklist). */
  onMobileViewed?: () => void
}

/**
 * The real campaign page in an iframe (`/__preview?mode=campaign`), with the builder's unsaved edits sent
 * over postMessage as they happen (FR-CB7). Only messages from the storefront origin are accepted.
 */
export const CampaignPreviewPane = ({ origin, offer, draft, onMobileViewed }: Props) => {
  const frame = useRef<HTMLIFrameElement>(null)
  const [src, setSrc] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [device, setDevice] = useState<Device>('mobile')
  const [reloadKey, setReloadKey] = useState(0)
  const onMobileViewedRef = useRef(onMobileViewed)
  onMobileViewedRef.current = onMobileViewed

  useEffect(() => {
    let cancelled = false
    setReady(false)
    setError(null)
    setSrc(null)
    mintPreviewToken(origin, { offerId: offer.id })
      .then((token) => {
        if (cancelled) return
        const q = new URLSearchParams({ mode: 'campaign', offer: offer.id, slug: offer.slug, token })
        setSrc(`${origin}/__preview?${q}`)
      })
      .catch(() => !cancelled && setError('Could not start the preview. Check that you are signed in and the store is reachable.'))
    return () => { cancelled = true }
  }, [origin, offer.id, offer.slug, reloadKey])

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== new URL(origin).origin || event.source !== frame.current?.contentWindow) return
      const msg = parsePreviewMessage(event.data)
      if (msg?.type === 'ready') setReady(true)
      else if (msg?.type === 'error') setError(msg.message)
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [origin])

  // Send the draft shortly after each change (and as soon as the preview is ready).
  useEffect(() => {
    if (!ready) return
    const timer = setTimeout(() => {
      const message: BuilderToPreview = { v: 1, type: 'campaign:replace', offer: draft }
      frame.current?.contentWindow?.postMessage(message, new URL(origin).origin)
    }, 250)
    return () => clearTimeout(timer)
  }, [ready, draft, origin])

  useEffect(() => {
    if (device === 'mobile' && ready) onMobileViewedRef.current?.()
  }, [device, ready])

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-border bg-card shadow-z1">
      <div className="flex items-center justify-between gap-2 border-b border-border-subtle px-3 py-2">
        <div className="flex gap-1" role="group" aria-label="Preview size">
          {(['mobile', 'desktop'] as const).map((d) => (
            <Button
              key={d}
              variant={device === d ? 'secondary' : 'ghost'}
              aria-pressed={device === d}
              onClick={() => setDevice(d)}
            >
              {d === 'mobile' ? <Smartphone /> : <Monitor />}
              {d === 'mobile' ? 'Phone' : 'Computer'}
            </Button>
          ))}
        </div>
        <span className="truncate text-xs text-muted-foreground">{ready ? 'Live — changes show as you edit' : error ? '' : 'Loading preview…'}</span>
        <Button size="icon" variant="ghost" aria-label="Reload preview" onClick={() => setReloadKey((k) => k + 1)}>
          <RefreshCw />
        </Button>
      </div>
      <div className="flex flex-1 justify-center overflow-auto bg-subdued p-3">
        {error ? (
          <p className="m-auto max-w-xs text-center text-sm text-muted-foreground">{error}</p>
        ) : src ? (
          <iframe
            ref={frame}
            key={src}
            src={src}
            title="Campaign preview"
            className={cn('h-full min-h-[640px] rounded-lg border border-border bg-card shadow-z1', device === 'mobile' ? 'w-[390px]' : 'w-full')}
          />
        ) : null}
      </div>
    </div>
  )
}
